import React from 'react';
import { Link } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import Picture from '../components/Picture';
import SEO from '../components/SEO';

const HERO_IMG = '/images/sri-lanka.jpg';
const ACTIVITIES_IMG = '/images/activities.jpeg';

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About Us"
        description="Learn about Glossop & District Round Table 916 — life-changing experiences, friends for life, and millions raised for charity since 1927. Part of the Round Table family including Ladies Circle, 41 Club and Tangent."
        path="/about"
        keywords={['about Round Table', 'Round Table history', 'Round Table family', 'Ladies Circle', '41 Club', 'community organisation Glossop']}
      />
      {/* Hero */}
      <HeroSection
        image={HERO_IMG}
        title="About Us"
        subtitle="Round Table is life changing"
        fullHeight={false}
      />

      {/* Main Content */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500] mb-4 block">
                Round Table Is Fun, Friendship And Fundraising!
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-6">
                Life Changing Experiences
              </h2>
              <div className="space-y-4 text-base text-[#52525B] leading-relaxed">
                <p>
                  Round Table is life changing. To its members, it gives friends for life from all around the world. It provides experiences, travels and so many fantastic events that will both inspire and drive you to #DoMore. Get ready to take on the{' '}
                  <a href="https://www.tickettailor.com/events/roundtabletickets/1833090" target="_blank" rel="noopener noreferrer" className="text-[#F0A500] hover:text-[#D99500] font-semibold transition-colors">
                    National Sporting Weekend
                  </a>{' '}
                  with Table members, or perhaps try your hand at sausage making.
                </p>
                <p>
                  Plus, every year the Association raises millions of pounds for good causes and charities, whilst inspiring thousands of people to help their local communities. Get involved in organising one of the biggest firework displays in the country to support your chosen local charity, or run the best beer festival your town has ever seen with a great bunch of guys. There is no limit to where your Round Table experience can take you!
                </p>
                <p>
                  Round Table also offers a number of exclusive offers to Table members. If you have questions about the financial benefits of Round Table?{' '}
                  <a href="https://www.roundtable.co.uk/faq" target="_blank" rel="noopener noreferrer" className="text-[#F0A500] hover:text-[#D99500] font-semibold transition-colors">
                    Click here
                  </a>.
                </p>
              </div>
            </div>
            <div>
              <Picture
                src={ACTIVITIES_IMG}
                alt="Round Table activities"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Round Table Family */}
      <section data-testid="about-family-section" className="py-20 sm:py-28 bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500] mb-4 block">
              Part of Something Bigger
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
              The Round Table Family
            </h2>
            <p className="text-base text-zinc-400 max-w-2xl mx-auto">
              Round Table is just one of a family of organisations, giving back to their community and providing the chance to try something new.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                name: 'Ladies Circle',
                desc: 'Open to women aged 18-45, sharing the same core values of friends, having fun and supporting local causes.',
                url: 'http://www.ladiescircle.co.uk/',
              },
              {
                name: '41 Club',
                desc: 'The association of ex-Tablers. Once members have left Round Table at 45, they can continue the camaraderie.',
                url: 'http://41club.org/',
              },
              {
                name: 'Tangent',
                desc: 'For former Ladies Circle members continuing their journey of community service and friendship.',
                url: 'https://www.tangent-clubs.org/',
              },
              {
                name: 'RT International',
                desc: 'A truly international organisation with over 35,000 members in 60 different countries.',
                url: 'http://rtinternational.org/',
              },
            ].map((org) => (
              <a
                key={org.name}
                href={org.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`about-family-${org.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="group block p-6 border border-zinc-800 hover:border-[#F0A500] transition-colors"
              >
                <h3 className="text-lg font-bold mb-2 group-hover:text-[#F0A500] transition-colors">
                  {org.name}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {org.desc}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28 bg-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-4">
            Ready to Do More?
          </h2>
          <p className="text-base text-[#52525B] mb-8">
            Round Table stands for fun and friendship, service in our communities and unique and everlasting friendships which have no borders.
          </p>
          <Link
            to="/membership"
            data-testid="about-join-cta-btn"
            className="btn-amber inline-block"
          >
            Join Round Table
          </Link>
        </div>
      </section>
    </div>
  );
}
